export const CONSENT_KEY = "bondIT_cookie_consent";
const SIX_MONTHS_MS = 6 * 30 * 24 * 60 * 60 * 1000;

export interface ConsentState {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  timestamp: number;
}

export function loadStoredConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as ConsentState;
    if (Date.now() - parsed.timestamp > SIX_MONTHS_MS) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(consent: ConsentState): void {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
}

let rybbitLoaded = false;

/**
 * Events tracked with analytics consent while the Rybbit script is still
 * loading. `ConsentProvider` starts the script during its first render, but
 * `window.rybbit` only exists once the script has run — so on a cold page
 * load, events sent from mount effects (the first `signup_step`,
 * `view_hour_reporting`) would otherwise be dropped (#911). Nothing is held
 * without consent, and the list is capped in case the script never loads
 * (blocked, or analytics not configured).
 */
const MAX_PENDING_EVENTS = 20;
let pendingEvents: { name: AnalyticsEventName; props: Record<string, string> }[] = [];
const readyListeners = new Set<() => void>();

const rybbitReady = (): boolean =>
  rybbitLoaded && typeof window !== "undefined" && window.rybbit !== undefined;

function sendToRybbit(name: AnalyticsEventName, props: Record<string, string>): void {
  // Events fire inside success paths (a saved password, a submitted
  // timesheet): a failing third-party script must never turn those into an
  // error for the user.
  try {
    window.rybbit?.event(name, props);
  } catch (error) {
    console.warn("Analytics event could not be sent:", name, error);
  }
}

/** The script defined `window.rybbit`: send what was held, then tell subscribers. */
function handleRybbitReady(): void {
  if (!rybbitReady()) {
    return;
  }
  const held = pendingEvents;
  pendingEvents = [];
  if (loadStoredConsent()?.analytics) {
    held.forEach(({ name, props }) => sendToRybbit(name, props));
  }
  readyListeners.forEach(listener => listener());
}

/**
 * Calls `listener` each time the Rybbit script becomes ready — on its first
 * load, and again when analytics is re-enabled. Lets a component report state
 * that was already on screen when the visitor accepted analytics. Returns the
 * unsubscribe function.
 */
export function onAnalyticsReady(listener: () => void): () => void {
  readyListeners.add(listener);
  return () => {
    readyListeners.delete(listener);
  };
}

/**
 * Load the Rybbit analytics script when the user consents.
 * Reads the script URL and site ID from window.APP_CONFIG
 * (injected at container startup by docker-entrypoint.sh).
 */
export function initGA(consent: ConsentState): void {
  if (!consent.analytics) {
    rybbitLoaded = false;
    pendingEvents = [];
    return;
  }

  const config = window.APP_CONFIG;
  if (!config?.ANALYTICS_URL || !config?.ANALYTICS_SITE_ID || rybbitLoaded) {
    return;
  }

  const existing = document.querySelector<HTMLScriptElement>(
    'script[data-bondit-analytics="rybbit"]'
  );
  if (existing) {
    // Analytics re-enabled on a page that already has the script. If it is
    // still loading, the load listener attached when it was created runs later.
    rybbitLoaded = true;
    handleRybbitReady();
    return;
  }

  const script = document.createElement("script");
  script.src = config.ANALYTICS_URL;
  script.defer = true;
  script.crossOrigin = "anonymous";
  script.setAttribute("data-site-id", config.ANALYTICS_SITE_ID);
  script.setAttribute("data-bondit-analytics", "rybbit");
  // The tracker defines `window.rybbit` synchronously when it runs, so it
  // exists by the time `load` fires.
  script.addEventListener("load", handleRybbitReady);
  document.head.appendChild(script);

  rybbitLoaded = true;
}

export function trackPageView(_path: string): void {
  const consent = loadStoredConsent();
  if (!consent?.analytics || !rybbitLoaded) {
    return;
  }
  // Rybbit auto-tracks SPA page views via history API.
  // Explicit pageview as a safety net.
  if (typeof window !== "undefined" && window.rybbit?.pageview) {
    window.rybbit.pageview();
  }
}

/**
 * Every custom event the app sends to Rybbit. Rybbit goals and funnels match
 * these names exactly, so renaming one silently breaks them — the catalogue
 * and the recommended goal/funnel setup live in
 * `docs/analytics/rybbit-goals-and-funnels.md`; keep the two in step.
 *
 * Props must stay coarse (a step number, a source, a role): never names,
 * emails, ids, rates or tokens.
 */
export type AnalyticsEventName =
  | "login"
  | "logout"
  | "dev_login"
  | "view_assignment"
  | "view_hour_reporting"
  | "help_popup_opened"
  | "theme_toggle"
  | "signup_step"
  | "signup_email_verified"
  | "signup_submitted"
  | "password_set"
  | "assignment_applied"
  | "hours_logged"
  | "timesheet_submitted";

/**
 * Sends a custom event to Rybbit. Does nothing without analytics consent.
 * With consent, an event tracked while the script is still loading is held
 * and sent once it has loaded.
 *
 * Returns `true` when the event was sent or is held for sending, and `false`
 * when it was dropped for lack of analytics consent.
 */
export function trackEvent(
  name: AnalyticsEventName,
  params?: Record<string, string | number | boolean>
): boolean {
  const consent = loadStoredConsent();
  if (!consent?.analytics) {
    return false;
  }
  const props: Record<string, string> = {};
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      props[k] = String(v);
    }
  }
  if (!rybbitReady()) {
    if (pendingEvents.length < MAX_PENDING_EVENTS) {
      pendingEvents.push({ name, props });
    }
    return true;
  }
  sendToRybbit(name, props);
  return true;
}
